import type { Capability, Plan, Role } from './types'

/**
 * Client-side mirror of the server's role matrix.
 *
 * This exists to decide what to *show* — a viewer should not be offered an
 * Invite button they cannot use. It is never a security boundary: every one of
 * these capabilities is re-checked server-side on the request itself, and the
 * UI treats a 403 as the authoritative answer.
 */
const MIN_ROLE: Record<Capability, Role> = {
  'org.view': 'viewer',
  'member.view': 'viewer',
  'content.write': 'member',
  'org.update': 'admin',
  'member.invite': 'admin',
  'member.remove': 'admin',
  'member.change_role': 'admin',
  'org.delete': 'owner',
  'org.transfer': 'owner',
  'org.billing': 'owner',
}

/** Ascending privilege. An unknown role ranks below everything and is denied. */
const RANK: Record<Role, number> = { viewer: 1, member: 2, admin: 3, owner: 4 }

export function roleRank(role: string | null | undefined): number {
  return RANK[role as Role] ?? 0
}

/** True when `role` is at least as privileged as `required`. */
export function roleAtLeast(role: string | null | undefined, required: Role): boolean {
  return roleRank(role) >= RANK[required]
}

/** Default-deny for unknown roles and unknown capabilities alike. */
export function roleCan(role: string | null | undefined, capability: Capability): boolean {
  const min = MIN_ROLE[capability]
  if (!min) return false
  return roleAtLeast(role, min)
}

/** Roles an admin may assign. `owner` is excluded: it moves only by transfer. */
export const ASSIGNABLE_ROLES: Role[] = ['admin', 'member', 'viewer']

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Full control, including billing and deleting the workspace.',
  admin: 'Manage members, invitations and workspace settings.',
  member: 'Create and edit projects, issues, sprints and notes.',
  viewer: 'Read-only access to everything in the workspace.',
}

/**
 * Whether a plan id is a paid one.
 *
 * Spelled as "not free" rather than "=== 'pro'" on purpose. The web ladder is
 * standard/business/unlimited and `pro` is only what an App Store subscription
 * grants, so an equality check against a single id shows the upgrade nag to
 * paying customers.
 */
export function isPaidPlan(plan: Plan | undefined | null): boolean {
  return !!plan && plan !== 'free'
}
